import { create } from "zustand";
import { toast } from "sonner";
import confetti from "canvas-confetti";

interface OutreachState {
  isLoading: boolean;
  error: string | null;
  /**
   * The Launch button, every campaign (Amy-run or not). `refreshCreatorData`
   * = the dialog's "Refresh creator data first" switch: the backend then only
   * queues the refresh, and the maintenance worker launches the campaign
   * when it finishes.
   */
  startInitialOutreach: (
    campaignId: string,
    token: string,
    refreshCreatorData?: boolean
  ) => Promise<void>;
  reset: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const useOutreachStore = create<OutreachState>((set) => ({
  isLoading: false,
  error: null,

  startInitialOutreach: async (
    campaignId: string,
    token: string,
    refreshCreatorData: boolean = false
  ) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/campaigns/startInitialOutreach`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_id: campaignId,
            refresh_creator_data: refreshCreatorData,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        toast.error(data.message || data.error || "Failed to start outreach sequence!");
        throw new Error(data.message || "Failed to start outreach sequence");
      }

      if (data.creator_refresh?.queued) {
        toast.info("Refreshing creator data first, the campaign launches when it's done.");
      } else {
        confetti({
          particleCount: 150,
          spread: 90,
          origin: { y: 0.2, x: 0.9 }
        });
      }
      set({ isLoading: false });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  reset: () => set({ isLoading: false, error: null }),
}));
